import { registerStyles } from "../utils/registerStyles";
// src/pages/EmployeesPage.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import { normalizeWorkTimeMeasure, workTimeHoursLabel } from "../utils/workTimeMeasure";
import LoadingIndicator from "../components/LoadingIndicator";
import { FaChevronDown, FaPlus, FaTimes, FaCalendarPlus, FaTrash } from "react-icons/fa";
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  getDocs,
  deleteDoc,
  getDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import ConnectedSchedulingPopup from "../popup/ConnectedSchedulingPopup";

function WorkTimeField({ value, options, onChange, onCreate, onDelete, disabled }) {
  const [open, setOpen] = useState(false);
  const [percent, setPercent] = useState("");
  const [hours, setHours] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const busy = saving || !!deletingId;

  const close = () => {
    if (busy) return;
    setOpen(false);
    setPercent("");
    setHours("");
    setError("");
  };
  const save = async (event) => {
    event.preventDefault();
    if (busy || disabled) return;
    const percentage = Number(percent.replace(",", "."));
    const monthlyHours = Number(hours.replace(",", "."));
    if (!percent.trim() || !hours.trim() || !Number.isFinite(percentage) || percentage < 0 || percentage > 100 ||
        !Number.isFinite(monthlyHours) || monthlyHours < 0 || monthlyHours > 744) {
      setError("Ange procent mellan 0 och 100 samt timmar mellan 0 och 744.");
      return;
    }
    const existing = options.find((option) => option.percentage === percentage && option.monthlyHours === monthlyHours);
    if (existing) {
      onChange(existing.id);
      close();
      return;
    }
    setSaving(true);
    setError("");
    try {
      const id = await onCreate({ percentage, monthlyHours });
      onChange(id);
      setOpen(false);
      setPercent("");
      setHours("");
    } catch (err) {
      setError("Kunde inte spara arbetstidsmåttet. Försök igen.");
    } finally {
      setSaving(false);
    }
  };

  const deleteWorkTime = async (option) => {
    if (busy || disabled || !window.confirm(`Ta bort arbetstidsmåttet ${workTimeLabel(option)}? Anställda med detta mått får Inget arbetstidsmått.`)) return;
    setDeletingId(option.id);
    setError("");
    try {
      await onDelete(option.id);
      if (value === option.id) onChange("");
    } catch (err) {
      setError("Kunde inte ta bort arbetstidsmåttet. Försök igen.");
    } finally {
      setDeletingId(null);
    }
  };

  return <div style={{ gridColumn: "span 2" }}>
    <label htmlFor="employee-work-time" style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, display: "block" }}>Arbetstidsmått</label>
    <div style={{ display: "flex", gap: 8 }}>
      <select id="employee-work-time" value={value || ""} onChange={(event) => onChange(event.target.value)} disabled={disabled || busy}
        style={{ ...inputStyle, paddingRight: 12, flex: 1 }}>
        <option value="">Inget arbetstidsmått</option>
        {options.map((option) => <option key={option.id} value={option.id}>{workTimeLabel(option)}</option>)}
      </select>
      <button type="button" className="employee-group-add" aria-label="Lägg till arbetstidsmått" title="Lägg till arbetstidsmått" disabled={disabled || busy} onClick={() => setOpen(true)}><FaPlus aria-hidden="true" /></button>
    </div>
    {open && <div className="employee-group-panel" role="dialog" aria-label="Lägg till arbetstidsmått">
      <div className="employee-group-panel-header"><h4>Arbetstidsmått</h4><button type="button" onClick={close} disabled={disabled || busy} aria-label="Stäng arbetstidsmått"><FaTimes /></button></div>
      <p>Ange sysselsättningsgrad och motsvarande antal timmar per månad. 0 % och 0 timmar undantar personen från arbetstids- och övertidsnotiser.</p>
      <form onSubmit={save}>
        <label htmlFor="work-time-percent">Procent (%)</label>
        <input id="work-time-percent" autoFocus inputMode="decimal" value={percent} onChange={(event) => setPercent(event.target.value)} placeholder="Exempel: 75" required disabled={disabled || busy} />
        <label htmlFor="work-time-hours">Timmar per månad</label>
        <input id="work-time-hours" inputMode="decimal" value={hours} onChange={(event) => setHours(event.target.value)} placeholder="Exempel: 165" required disabled={disabled || busy} />
        {error && <span className="employee-group-error" role="alert">{error}</span>}
        <div className="employee-group-panel-actions"><button type="button" onClick={close} disabled={disabled || busy}>Stäng</button><button type="submit" disabled={disabled || busy || !percent.trim() || !hours.trim()}>{saving ? "Sparar..." : "Spara"}</button></div>
      </form>
      <div className="employee-group-list">
        <h5>Skapade arbetstidsmått</h5>
        <div className="employee-group-list-items">
          {options.length ? options.map((option) => <div className="employee-group-list-row" key={option.id}>
            <span title={workTimeLabel(option)}>{workTimeLabel(option)}</span>
            <button type="button" title={`Ta bort ${workTimeLabel(option)}`} aria-label={`Ta bort arbetstidsmåttet ${workTimeLabel(option)}`}
              onClick={() => deleteWorkTime(option)} disabled={disabled || busy}>
              <FaTrash aria-hidden="true" />
            </button>
          </div>) : <p>Inga arbetstidsmått skapade ännu.</p>}
        </div>
      </div>
    </div>}
  </div>;
}

function workTimeLabel(option) {
  return `${Number(option.percentage).toLocaleString("sv-SE")} % – ${workTimeHoursLabel(option)}`;
}

function EmployeeGroupField({ value, groups, onChange, onCreate, onDelete, disabled }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const closePanel = () => {
    if (saving || deletingId) return;
    setPanelOpen(false);
    setName("");
    setError("");
  };

  const createGroup = async (event) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName || saving) return;
    if (groups.some((group) => group.name.toLocaleLowerCase("sv-SE") === trimmedName.toLocaleLowerCase("sv-SE"))) {
      setError("Gruppen finns redan.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const id = await onCreate(trimmedName);
      onChange(id);
      setName("");
    } catch (err) {
      setError("Kunde inte skapa gruppen. Försök igen.");
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async (group) => {
    if (saving || deletingId || !window.confirm(`Ta bort gruppen ${group.name}? Anställda i gruppen får Ingen grupp.`)) return;
    setDeletingId(group.id);
    setError("");
    try {
      await onDelete(group.id);
      if (value === group.id) onChange("");
    } catch (err) {
      setError("Kunde inte ta bort gruppen. Försök igen.");
    } finally {
      setDeletingId(null);
    }
  };

  return <div style={{ gridColumn: "span 2" }}>
    <label htmlFor="employee-group-select" style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, display: "block" }}>Grupp</label>
    <div style={{ display: "flex", gap: 8 }}>
      <select id="employee-group-select" value={value || ""} onChange={(event) => onChange(event.target.value)} disabled={disabled}
        style={{ ...inputStyle, paddingRight: 12, flex: 1 }}>
        <option value="">Ingen grupp</option>
        {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
      </select>
      <button type="button" aria-label="Lägg till grupp" title="Lägg till grupp" disabled={disabled}
        onClick={() => setPanelOpen(true)} className="employee-group-add"><FaPlus aria-hidden="true" /></button>
    </div>
    {panelOpen && <div className="employee-group-panel" role="dialog" aria-label="Lägg till grupp">
      <div className="employee-group-panel-header"><h4>Grupper</h4><button type="button" onClick={closePanel} disabled={saving || !!deletingId} aria-label="Stäng gruppanel"><FaTimes /></button></div>
      <p>Skapa en grupp och välj den för den anställde.</p>
      <form onSubmit={createGroup}>
        <label htmlFor="new-employee-group">Gruppnamn</label>
        <input id="new-employee-group" autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder="Ange gruppnamn" maxLength={80} disabled={saving || !!deletingId} />
        {error && <span role="alert" className="employee-group-error">{error}</span>}
        <div className="employee-group-panel-actions"><button type="button" onClick={closePanel} disabled={saving || !!deletingId}>Stäng</button><button type="submit" disabled={!name.trim() || saving || !!deletingId}>{saving ? "Sparar..." : "Lägg till grupp"}</button></div>
      </form>
      <div className="employee-group-list">
        <h5>Skapade grupper</h5>
        <div className="employee-group-list-items">
          {groups.length ? groups.map((group) => <div className="employee-group-list-row" key={group.id}>
            <span title={group.name}>{group.name}</span>
            <button type="button" title={`Ta bort ${group.name}`} aria-label={`Ta bort gruppen ${group.name}`}
              onClick={() => deleteGroup(group)} disabled={saving || !!deletingId}>
              <FaTrash aria-hidden="true" />
            </button>
          </div>) : <p>Inga grupper skapade ännu.</p>}
        </div>
      </div>
    </div>}
  </div>;
}
 
// Global cache - ENDAST FÖR OWNER INFO, INTE FÖR ANSTÄLLDA
const employeeCache = {
  ownerInfo: null, // Sparar bara ägarinfo för att kunna lägga till i listan
};

const EMPLOYEE_TABLE_COLUMNS = "minmax(120px, 1fr) minmax(180px, 2fr) minmax(210px, 1.5fr) minmax(150px, 1fr) minmax(110px, 1fr) minmax(110px, 1fr) 56px";
 
const boxStyle = {
  position: "relative",
  display: "inline-block",
  flex: "0 1 220px",
  minWidth: 150,
  maxWidth: 260,
};
 
const inputStyle = {
  width: "100%",
  height: 50,
  padding: "8px 34px 8px 12px",
  border: "1px solid #d1d5db",
  borderRadius: 3,
  background: "#ffffff",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  boxSizing: "border-box",
};
 
const labelStyle = {
  position: "absolute",
  top: -10,
  left: 12,
  fontSize: "0.7rem",
  background: "#ffffff",
  padding: "0 4px",
  color: "#6b7280",
  userSelect: "none",
};
 
const toolbarButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 42,
  height: 42,
  borderRadius: 8,
  color: "white",
  fontSize: 18,
  padding: 0,
  boxSizing: "border-box",
  cursor: "pointer",
};
 
const ROLE_LABELS_SV = {
  "super-admin": "Super-admin",
  admin: "Admin",
  user: "Användare",
};
 
const STATUS_LABELS_SV = {
  active: "Aktiv",
  pending: "Inväntar",
  expired: "Utgången",
  invited: "Inbjuden",
  disabled: "Inaktiverad",
};
 
const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i <= 5; i++) {
    years.push((currentYear - i).toString());
  }
  return years;
};
 
const getMonthOptions = () => {
  return [
    "Januari", "Februari", "Mars", "April", "Maj", "Juni",
    "Juli", "Augusti", "September", "Oktober", "November", "December"
  ];
};
 
const getCurrentYear = () => new Date().getFullYear().toString();
const getCurrentMonth = () => {
  const months = getMonthOptions();
  return months[new Date().getMonth()];
};
 
function generateToken() {
  const cryptoObj = window.crypto || window.msCrypto;
  const array = new Uint8Array(32);
  cryptoObj.getRandomValues(array);
  return Array.from(array, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
}
 
function getSafeEmailDocId(email = "") {
  return email.trim().toLowerCase().replace(/[.#$/[\]]/g, "_");
}
 
function normalizeEmail(email = "") {
  return email.trim().toLowerCase();
}

function requiredNumber(value) {
  const number = String(value || "").trim();
  return number === "–" ? "" : number;
}
 
async function updateEmployeeCount(companyAccountUid) {
  if (!companyAccountUid) return;
  
  try {
    const employedRef = collection(db, "users", companyAccountUid, "employed");
    const snapshot = await getDocs(employedRef);
    const newCount = snapshot.size;
    
    const companyDocRef = doc(db, "users", companyAccountUid);
    await updateDoc(companyDocRef, {
      employeeCount: newCount,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Uppdaterade employeeCount till ${newCount} för företag: ${companyAccountUid}`);
    return newCount;
  } catch (error) {
    console.error("Fel vid uppdatering av employeeCount:", error);
    return 0;
  }
}
 
export default function EmployeesPage() {
  const { currentUser, profile } = useAuth();
  const { activeCompany, loading: companyLoading, allowedSchemaIds, permissionsLoading } = useCompany();
 
  const currentRole = activeCompany?.role || "user";
 
  // Behörighet:
  // - Super-admin får redigera alla, inklusive sitt eget konto.
  // - Admin får redigera admin/användare, men aldrig Super-admin.
  // - Vanlig användare får inte redigera någon.
  const canEditEmployee = (employee) => {
    if (!employee) return false;
    if (currentRole === "super-admin") return true;
    if (currentRole === "admin") return employee.role !== "super-admin";
    return false;
  };
 
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState(getCurrentYear());
  const [monthFilter, setMonthFilter] = useState(getCurrentMonth());
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showSchedulingPopup, setShowSchedulingPopup] = useState(false);
  const [unassignedEmployeeCount, setUnassignedEmployeeCount] = useState(0);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResendConfirm, setShowResendConfirm] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [savingError, setSavingError] = useState("");
  const [editError, setEditError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [workTimeOptions, setWorkTimeOptions] = useState([]);
 
  const [newEmployee, setNewEmployee] = useState({
    firstName: "",
    lastName: "",
    email: "",
    personalNumber: "",
    employmentNumber: "",
    phoneNumber: "",
    role: "user",
    workTimeId: "",
    groupId: "",
  });

  const employmentNumberTaken = (number, currentEmployeeId = null) => {
    const normalized = requiredNumber(number).toLocaleLowerCase("sv-SE");
    return !!normalized && employees.some((employee) =>
      employee.companyAccountUid === activeCompany?.companyAccountUid &&
      employee.id !== currentEmployeeId &&
      requiredNumber(employee.employmentNumber).toLocaleLowerCase("sv-SE") === normalized
    );
  };

  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
    if (!accountToUse) {
      setEmployeeGroups([]);
      return;
    }
    return onSnapshot(collection(db, "users", accountToUse, "employeeGroups"),
      (snapshot) => setEmployeeGroups(snapshot.docs.map((groupDoc) => ({ id: groupDoc.id, ...groupDoc.data() }))
        .sort((a, b) => a.name.localeCompare(b.name, "sv-SE"))),
      (error) => console.error("Kunde inte hämta grupper:", error));
  }, [activeCompany?.companyAccountUid]);

  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
    setWorkTimeOptions([]);
    if (!accountToUse) return;
    return onSnapshot(collection(db, "users", accountToUse, "workTimeMeasures"),
      (snapshot) => setWorkTimeOptions(snapshot.docs.map((entry) => normalizeWorkTimeMeasure({ ...entry.data(), id: entry.id })).filter(Boolean)
        .sort((a, b) => b.percentage - a.percentage || b.monthlyHours - a.monthlyHours)),
      (error) => console.error("Kunde inte hämta arbetstidsmått:", error));
  }, [activeCompany?.companyAccountUid]);

  const createWorkTime = async ({ percentage, monthlyHours }) => {
    const accountToUse = activeCompany?.companyAccountUid;
    if (!accountToUse) throw new Error("Inget företagskonto hittades.");
    const id = `month_${percentage}_${monthlyHours}`;
    await setDoc(doc(db, "users", accountToUse, "workTimeMeasures", id), { percentage, monthlyHours });
    setWorkTimeOptions((current) => current.some((option) => option.id === id) ? current
      : [...current, { id, percentage, monthlyHours }].sort((a, b) => b.percentage - a.percentage));
    return id;
  };

  const deleteWorkTime = async (workTimeId) => {
    const accountToUse = activeCompany?.companyAccountUid;
    if (!accountToUse || !["admin", "super-admin"].includes(currentRole)) throw new Error("Behörighet saknas.");
    const [assignedEmployees, ownerSnapshot] = await Promise.all([
      getDocs(query(collection(db, "users", accountToUse, "employed"), where("workTimeId", "==", workTimeId))),
      getDoc(doc(db, "users", accountToUse)),
    ]);
    const assignedRefs = assignedEmployees.docs.map((employeeDoc) => employeeDoc.ref);
    if (ownerSnapshot.exists() && ownerSnapshot.data().workTimeId === workTimeId) assignedRefs.push(ownerSnapshot.ref);
    for (let offset = 0; offset < assignedRefs.length; offset += 400) {
      const batch = writeBatch(db);
      assignedRefs.slice(offset, offset + 400).forEach((ref) => batch.update(ref, { workTimeId: "" }));
      await batch.commit();
    }
    await deleteDoc(doc(db, "users", accountToUse, "workTimeMeasures", workTimeId));
    setWorkTimeOptions((current) => current.filter((option) => option.id !== workTimeId));
    setNewEmployee((current) => current.workTimeId === workTimeId ? { ...current, workTimeId: "" } : current);
    setSelectedEmployee((current) => current?.workTimeId === workTimeId ? { ...current, workTimeId: "" } : current);
  };

  const createEmployeeGroup = async (name) => {
    const accountToUse = activeCompany?.companyAccountUid;
    if (!accountToUse) throw new Error("Inget företagskonto hittades.");
    const groupDoc = await addDoc(collection(db, "users", accountToUse, "employeeGroups"), {
      name,
      createdAt: serverTimestamp(),
    });
    setEmployeeGroups((current) => current.some((group) => group.id === groupDoc.id)
      ? current
      : [...current, { id: groupDoc.id, name }].sort((a, b) => a.name.localeCompare(b.name, "sv-SE")));
    return groupDoc.id;
  };

  const deleteEmployeeGroup = async (groupId) => {
    const accountToUse = activeCompany?.companyAccountUid;
    if (!accountToUse) throw new Error("Inget företagskonto hittades.");
    const [assignedEmployees, ownerSnapshot] = await Promise.all([
      getDocs(query(collection(db, "users", accountToUse, "employed"), where("groupId", "==", groupId))),
      getDoc(doc(db, "users", accountToUse)),
    ]);
    const assignedRefs = assignedEmployees.docs.map((employeeDoc) => employeeDoc.ref);
    if (ownerSnapshot.exists() && ownerSnapshot.data().groupId === groupId) assignedRefs.push(ownerSnapshot.ref);

    for (let offset = 0; offset < assignedRefs.length; offset += 400) {
      const batch = writeBatch(db);
      assignedRefs.slice(offset, offset + 400).forEach((ref) => batch.update(ref, { groupId: "" }));
      await batch.commit();
    }
    await deleteDoc(doc(db, "users", accountToUse, "employeeGroups", groupId));
    setEmployeeGroups((current) => current.filter((group) => group.id !== groupId));
  };
 
  const ownerInfoRef = useRef(null);
  const unsubscribeRef = useRef(null);

  // Hämta företagsinfo baserat på aktivt företag
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
    ownerInfoRef.current = null;
    employeeCache.ownerInfo = null;
    let subscribed = true;
 
    if (!accountToUse) {
      setCompanyInfo(null);
      ownerInfoRef.current = null;
      employeeCache.ownerInfo = null;
      return;
    }
 
    const userRef = doc(db, "users", accountToUse);
 
    const unsubscribe = onSnapshot(
      userRef,
      (userSnap) => {
        if (!subscribed) return;
        if (userSnap.exists()) {
          const data = userSnap.data();
 
          let createdAtDate = null;
          if (data.createdAt) {
            createdAtDate = data.createdAt?.toDate
              ? data.createdAt.toDate()
              : new Date(data.createdAt);
          }
 
          const info = {
            id: userSnap.id,
            companyId: data.companyId || profile?.companyId || "",
            companyName: data.companyName || profile?.companyName || "Företag",
            companyEmail: data.companyEmail || "company@lunero.se",
            email: data.email || currentUser?.email || "E-post saknas",
            role: data.role || profile?.role || "admin",
            createdAt: createdAtDate,
            employeeCount: data.employeeCount || 0,
          };
          
          setCompanyInfo(info);
          
          // Spara ägarinfo i ref och cache - ANVÄND data.firstName och data.lastName
          if (info.role === "super-admin") {
            let fullName = info.companyName || "Företagsägare";
            let firstNameValue = info.companyName || "Företagsägare";
            let lastNameValue = "";
            
            if (data.firstName && data.lastName) {
              fullName = `${data.firstName} ${data.lastName}`;
              firstNameValue = data.firstName;
              lastNameValue = data.lastName;
            } else if (data.firstName) {
              fullName = data.firstName;
              firstNameValue = data.firstName;
            } else if (data.lastName) {
              fullName = data.lastName;
              lastNameValue = data.lastName;
            }
            
            ownerInfoRef.current = {
              companyAccountUid: accountToUse,
              id: info.id,
              name: fullName,
              firstName: firstNameValue,
              lastName: lastNameValue,
              email: info.email || "E-post saknas",
              personalNumber: data.personalNumber || "–",
              employmentNumber: data.employmentNumber || "–",
              phoneNumber: data.phoneNumber || "–",
              role: "super-admin",
              workTimeId: data.workTimeId || "",
              groupId: data.groupId || "",
              membershipStatus: "active",
              createdAt: info.createdAt || null,
              authUid: info.id,
              isOwner: true,
              companyName: info.companyName || "Företag",
            };
            employeeCache.ownerInfo = ownerInfoRef.current;
 
            // Synkronisera Super-admin-raden direkt när users/{companyUid} ändras.
            // employed-listenern lyssnar bara på underkollektionen employed och
            // uppdaterar därför inte automatiskt ägarens namn/personuppgifter.
            setEmployees((previousEmployees) => {
              const withoutOldOwner = previousEmployees.filter(
                (employee) => employee.isOwner !== true && employee.companyAccountUid === accountToUse
              );
 
              const updatedEmployees = [
                ...withoutOldOwner,
                ownerInfoRef.current,
              ];
 
              const roleOrder = {
                "super-admin": 1,
                admin: 2,
                user: 3,
              };
 
              return updatedEmployees.sort((a, b) => {
                const roleDifference =
                  (roleOrder[a.role] || 99) - (roleOrder[b.role] || 99);
 
                if (roleDifference !== 0) return roleDifference;
                return (a.name || "").localeCompare(b.name || "");
              });
            });
          }
          
          if (data.employeeCount !== undefined) {
            setEmployeeCount(data.employeeCount);
          }
        } else {
          const info = {
            id: accountToUse,
            companyId: profile?.companyId || "",
            companyName: profile?.companyName || "Företag",
            companyEmail: "company@lunero.se",
            email: currentUser?.email || "E-post saknas",
            role: profile?.role || "admin",
            createdAt: null,
            employeeCount: 0,
          };
          setCompanyInfo(info);
          setEmployeeCount(0);
          
          if (info.role === "super-admin") {
            let fullName = info.companyName || "Företagsägare";
            let firstNameValue = info.companyName || "Företagsägare";
            let lastNameValue = "";
            
            if (profile?.firstName && profile?.lastName) {
              fullName = `${profile.firstName} ${profile.lastName}`;
              firstNameValue = profile.firstName;
              lastNameValue = profile.lastName;
            } else if (profile?.firstName) {
              fullName = profile.firstName;
              firstNameValue = profile.firstName;
            } else if (profile?.lastName) {
              fullName = profile.lastName;
              lastNameValue = profile.lastName;
            }
            
            ownerInfoRef.current = {
              companyAccountUid: accountToUse,
              id: info.id,
              name: fullName,
              firstName: firstNameValue,
              lastName: lastNameValue,
              email: info.email || "E-post saknas",
              personalNumber: "–",
              employmentNumber: "–",
              phoneNumber: "–",
              role: "super-admin",
              workTimeId: "",
              groupId: "",
              membershipStatus: "active",
              createdAt: null,
              authUid: info.id,
              isOwner: true,
              companyName: info.companyName || "Företag",
            };
            employeeCache.ownerInfo = ownerInfoRef.current;
          }
        }
      },
      (error) => {
        console.error("Fel vid hämtning av företagsinfo:", error);
      }
    );
 
    return () => {
      subscribed = false;
      unsubscribe();
    };
  }, [activeCompany?.companyAccountUid, currentUser?.email, profile?.companyId, profile?.companyName, profile?.role, profile?.firstName, profile?.lastName]);
 
  // Hämta anställda direkt från Firestore - INGEN CACHE
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
    let subscribed = true;
    setEmployees([]);
 
    if (!accountToUse) {
      setEmployees([]);
      setLoading(false);
      ownerInfoRef.current = null;
      return;
    }
 
    console.log(`🔄 Hämtar anställda för företag: ${accountToUse}`);
    setLoading(true);
 
    // Stäng av tidigare prenumeration
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
 
    const employedRef = collection(db, "users", accountToUse, "employed");
 
    const unsubscribe = onSnapshot(
      employedRef,
      async (snapshot) => {
        if (!subscribed) return;
        const users = [];
 
        snapshot.forEach((userDoc) => {
          const userData = userDoc.data();
 
          let fullName = userData.name || "";
          if (userData.firstName && userData.lastName) {
            fullName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData.firstName) {
            fullName = userData.firstName;
          } else if (userData.lastName) {
            fullName = userData.lastName;
          } else if (userData.displayName) {
            fullName = userData.displayName;
          } else if (userData.userName) {
            fullName = userData.userName;
          }
 
          users.push({
            ...userData,
            companyAccountUid: accountToUse,
            id: userDoc.id,
            name: fullName || "Användare",
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            email: userData.email || "E-post saknas",
            personalNumber: userData.personalNumber || "–",
            employmentNumber: userData.employmentNumber || "–",
            phoneNumber: userData.phoneNumber || "–",
            role: userData.role || "user",
            membershipStatus: userData.membershipStatus || userData.status || "invited",
            createdAt: userData.createdAt?.toDate
              ? userData.createdAt.toDate()
              : userData.createdAt
              ? new Date(userData.createdAt)
              : null,
            authUid: userData.authUid || null,
            companyName: userData.companyName || activeCompany?.companyName || "Företag",
          });
        });
 
        // Lägg till ägaren om den inte redan finns i listan
        const owner = ownerInfoRef.current;
        if (owner?.companyAccountUid === accountToUse) {
          const ownerExists = users.some(user => user.isOwner === true);
          if (!ownerExists) {
            users.push(owner);
          } else {
            // Uppdatera befintlig ägare med senaste data från cachen
            const index = users.findIndex(user => user.isOwner === true);
            if (index !== -1) {
              users[index] = owner;
            }
          }
        }
 
        // Sortera: super-admin först, sedan admin, sedan user
        const sortedUsers = users.sort((a, b) => {
          const roleOrder = {
            "super-admin": 1,
            admin: 2,
            user: 3,
          };
          const orderA = roleOrder[a.role] || 99;
          const orderB = roleOrder[b.role] || 99;
          if (orderA === orderB) {
            return (a.name || "").localeCompare(b.name || "");
          }
          return orderA - orderB;
        });
 
        setEmployees(sortedUsers);
        setLoading(false);
        
        // Uppdatera employeeCount
        await updateEmployeeCount(accountToUse);
      },
      (error) => {
        console.error("Fel vid realtidshämtning av anställda:", error);
        setLoading(false);
      }
    );
 
    unsubscribeRef.current = unsubscribe;
 
    return () => {
      subscribed = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [activeCompany?.companyAccountUid]);
 
  useEffect(() => {
    if (!currentUser) {
      console.log("👋 Användare loggade ut - rensar");
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      employeeCache.ownerInfo = null;
      ownerInfoRef.current = null;
      setEmployees([]);
    }
  }, [currentUser]);
 
  const filteredEmployees = useMemo(() => {
    let filtered = employees.filter((emp) => {
      const searchMatch =
        !searchTerm ||
        (emp.name &&
          emp.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.email &&
          emp.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.personalNumber &&
          emp.personalNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.employmentNumber &&
          emp.employmentNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.phoneNumber &&
          emp.phoneNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (ROLE_LABELS_SV[emp.role] &&
          ROLE_LABELS_SV[emp.role]
            .toLowerCase()
            .includes(searchTerm.toLowerCase()));
 
      const statusMatch =
        statusFilter === "all" || emp.membershipStatus === statusFilter;
      const roleMatch = roleFilter === "all" ||
        (roleFilter === "admin" ? emp.role === "admin" || emp.role === "super-admin" : emp.role === "user");
      const groupMatch = groupFilter === "all" ||
        (groupFilter === "ungrouped"
          ? !employeeGroups.some((group) => group.id === emp.groupId)
          : emp.groupId === groupFilter);
 
      let dateMatch = true;
      if (emp.createdAt && (yearFilter !== getCurrentYear() || monthFilter !== getCurrentMonth())) {
        const empYear = emp.createdAt.getFullYear().toString();
        const empMonth = getMonthOptions()[emp.createdAt.getMonth()];
        dateMatch = empYear === yearFilter && empMonth === monthFilter;
      } else if (yearFilter !== getCurrentYear() || monthFilter !== getCurrentMonth()) {
        dateMatch = false;
      }
 
      return searchMatch && statusMatch && roleMatch && groupMatch && dateMatch;
    });
    
    return filtered;
  }, [employees, searchTerm, statusFilter, roleFilter, groupFilter, employeeGroups, yearFilter, monthFilter]);

  const groupedEmployees = useMemo(() => {
    const groupsById = new Map(employeeGroups.map((group) => [group.id, group.name]));
    const sections = new Map();

    filteredEmployees.forEach((employee) => {
      const groupId = groupsById.has(employee.groupId) ? employee.groupId : "";
      if (!sections.has(groupId)) {
        sections.set(groupId, {
          id: groupId || "ungrouped",
          name: groupsById.get(groupId) || "Ingen grupp",
          employees: [],
        });
      }
      sections.get(groupId).employees.push(employee);
    });

    return [...sections.values()].sort((a, b) => {
      if (!a.id || a.id === "ungrouped") return 1;
      if (!b.id || b.id === "ungrouped") return -1;
      return a.name.localeCompare(b.name, "sv-SE");
    });
  }, [filteredEmployees, employeeGroups]);

  const formatDate = (date) => {
    if (!date) return "Okänt datum";
    return date.toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };
 
  const handleEditClick = (employee) => {
    if (!canEditEmployee(employee)) {
      alert(
        employee?.role === "super-admin"
          ? "Endast Super-admin kan redigera Super-admin."
          : "Du har inte behörighet att redigera den här användaren."
      );
      return;
    }
 
    setSelectedEmployee(employee);
    setShowDetailsPopup(false);
    setEditError("");
    setShowEditPopup(true);
  };
 
  const handleResendInvitation = async () => {
    const accountToUse = activeCompany?.companyAccountUid;
 
    if (!selectedEmployee || !accountToUse) return;
 
    try {
      setIsSubmitting(true);
 
      const normalizedEmail = normalizeEmail(selectedEmployee.email);
      const token = generateToken();
 
      const tokensQuery = query(
        collection(db, "invitationTokens"),
        where("email", "==", normalizedEmail),
        where("used", "==", false)
      );
 
      const existingTokens = await getDocs(tokensQuery);
 
      for (const tokenDoc of existingTokens.docs) {
        await updateDoc(doc(db, "invitationTokens", tokenDoc.id), {
          used: true,
          usedAt: serverTimestamp(),
        });
      }
 
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24);
 
      await setDoc(doc(db, "invitationTokens", token), {
        token,
        email: normalizedEmail,
        firstName: selectedEmployee.firstName || "",
        lastName: selectedEmployee.lastName || "",
        workTimeId: selectedEmployee.workTimeId || "",
        role: selectedEmployee.role || "user",
        personalNumber: requiredNumber(selectedEmployee.personalNumber),
        employmentNumber: requiredNumber(selectedEmployee.employmentNumber),
        companyId: companyInfo?.companyId || "",
        companyName: companyInfo?.companyName || "Företag",
        companyUserId: accountToUse,
        invitedBy: accountToUse,
        createdAt: serverTimestamp(),
        expiresAt,
        used: false,
        usedAt: null,
      });
 
      const registerUrl = `${window.location.origin}/register?token=${token}&company=${accountToUse}`;
 
      await addDoc(collection(db, "mail"), {
        to: [normalizedEmail],
        message: {
          subject: `Välkommen till ${companyInfo?.companyName || "företaget"}!`,
          text: `Hej ${selectedEmployee.firstName} ${selectedEmployee.lastName}!
 
Du har blivit inbjuden att gå med i ${companyInfo?.companyName || "företaget"}.
 
Dina uppgifter:
- E-post: ${normalizedEmail}
- Roll: ${ROLE_LABELS_SV[selectedEmployee.role] || selectedEmployee.role}
 
Registrera dig här: ${registerUrl}
 
Länken är giltig i 24 timmar och kan endast användas en gång.
 
Vänliga hälsningar,
${companyInfo?.companyName || "Företaget"}`,
          html: `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
            </head>
            <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
              <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e5e7eb; border-radius: 12px;">
                <h2 style="color: #2563eb; margin-bottom: 20px;">Välkommen till ${companyInfo?.companyName || "företaget"}!</h2>
                <p>Hej <strong>${selectedEmployee.firstName} ${selectedEmployee.lastName}</strong>,</p>
                <p>Du har blivit inbjuden att gå med i företaget. Klicka på knappen nedan för att registrera dig och skapa ditt konto.</p>
                <div style="background: #f9fafb; padding: 15px; border-radius: 8px; margin: 20px 0;">
                  <h3 style="margin-top: 0; color: #111827;">Dina uppgifter:</h3>
                  <ul style="margin: 0; padding-left: 20px;">
                    <li><strong>E-post:</strong> ${normalizedEmail}</li>
                    <li><strong>Roll:</strong> ${ROLE_LABELS_SV[selectedEmployee.role] || selectedEmployee.role}</li>
                  </ul>
                </div>
                <p><strong>Viktigt:</strong> Länken är giltig i 24 timmar och kan endast användas en gång.</p>
                <div style="text-align: center; margin: 30px 0;">
                  <a href="${registerUrl}" 
                     style="background-color: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: 600;">
                    Registrera dig här →
                  </a>
                </div>
                <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 20px 0;">
                <p style="font-size: 12px; color: #6b7280; text-align: center;">
                  Vänliga hälsningar,<br>
                  ${companyInfo?.companyName || "Företaget"}
                </p>
              </div>
            </body>
            </html>
          `,
        },
        delivery: {
          startTime: serverTimestamp(),
          state: "PENDING",
        },
        companyId: companyInfo?.companyId || "",
        companyName: companyInfo?.companyName || "Företag",
        companyUserId: accountToUse,
        sentTo: normalizedEmail,
        sentBy: accountToUse,
        sentAt: serverTimestamp(),
        type: "employee-invitation",
      });
 
      setShowResendConfirm(false);
      setShowEditPopup(false);
      setSelectedEmployee(null);
 
      alert(`Ny inbjudan skickades till ${normalizedEmail}!`);
    } catch (error) {
      console.error("Fel vid omskick av inbjudan:", error);
      alert("Det gick inte att skicka inbjudan igen. Försök igen.");
    } finally {
      setIsSubmitting(false);
    }
  };
 
  const handleSaveEmployee = async () => {
    const accountToUse = activeCompany?.companyAccountUid;
 
    if (!selectedEmployee || !accountToUse) return;
 
    if (!canEditEmployee(selectedEmployee)) {
      alert("Du har inte behörighet att redigera den här användaren.");
      return;
    }

    setEditError("");
    if (!requiredNumber(selectedEmployee.personalNumber)) {
      setEditError("Personnummer måste fyllas i.");
      return;
    }
    if (!requiredNumber(selectedEmployee.employmentNumber)) {
      setEditError("Anställningsnummer måste fyllas i.");
      return;
    }
    if (employmentNumberTaken(selectedEmployee.employmentNumber, selectedEmployee.id)) {
      setEditError("Anställningsnumret är upptaget av en annan anställd.");
      return;
    }
 
    try {
      setIsSubmitting(true);
 
      const firstName = selectedEmployee.firstName?.trim() || "";
      const lastName = selectedEmployee.lastName?.trim() || "";
      const fullName =
        firstName && lastName
          ? `${firstName} ${lastName}`
          : firstName || lastName || "Användare";
 
      // Super-adminens eget konto ligger i users/{companyAccountUid}.
      // E-post och roll är låsta för ägarkontot.
      if (selectedEmployee.isOwner && selectedEmployee.role === "super-admin") {
        const ownerDocId = selectedEmployee.authUid || selectedEmployee.id || accountToUse;
 
        const ownerUpdate = {
          firstName,
          lastName,
          name: fullName,
          displayName: fullName,
          personalNumber: requiredNumber(selectedEmployee.personalNumber),
          employmentNumber: requiredNumber(selectedEmployee.employmentNumber),
          phoneNumber: selectedEmployee.phoneNumber?.trim() || "–",
          workTimeId: selectedEmployee.workTimeId || "",
          groupId: selectedEmployee.groupId || "",
          updatedAt: serverTimestamp(),
        };
 
        // 1. Uppdatera huvud-dokumentet för super-admin
        await setDoc(doc(db, "users", ownerDocId), ownerUpdate, { merge: true });
 
        // 2. Uppdatera företags-dokumentet (companyInfo)
        if (ownerDocId !== accountToUse) {
          await setDoc(doc(db, "users", accountToUse), ownerUpdate, { merge: true });
        }
 
        // 3. Uppdatera även i employed-kollektionen om super-admin finns där
        try {
          const employedQuery = query(
            collection(db, "users", accountToUse, "employed"),
            where("email", "==", selectedEmployee.email)
          );
          const employedSnap = await getDocs(employedQuery);
          
          if (!employedSnap.empty) {
            for (const docSnap of employedSnap.docs) {
              await updateDoc(doc(db, "users", accountToUse, "employed", docSnap.id), {
                firstName,
                lastName,
                name: fullName,
                displayName: fullName,
                personalNumber: ownerUpdate.personalNumber,
                employmentNumber: ownerUpdate.employmentNumber,
                phoneNumber: ownerUpdate.phoneNumber,
                workTimeId: ownerUpdate.workTimeId,
                groupId: ownerUpdate.groupId,
                updatedAt: serverTimestamp(),
              });
            }
          }
        } catch (err) {
          console.log("Super-admin finns inte i employed-kollektionen (vilket är normalt)");
        }
 
        // 4. Uppdatera alla invitationTokens som är kopplade till denna email
        try {
          const tokensQuery = query(
            collection(db, "invitationTokens"),
            where("email", "==", selectedEmployee.email)
          );
          const tokensSnap = await getDocs(tokensQuery);
          
          for (const tokenDoc of tokensSnap.docs) {
            await updateDoc(doc(db, "invitationTokens", tokenDoc.id), {
              firstName,
              lastName,
              personalNumber: requiredNumber(selectedEmployee.personalNumber),
              employmentNumber: requiredNumber(selectedEmployee.employmentNumber),
              updatedAt: serverTimestamp(),
            });
          }
        } catch (err) {
          console.log("Inga invitationTokens hittades för denna email");
        }
 
        // 5. Uppdatera mail-kollektionen
        try {
          const mailQuery = query(
            collection(db, "mail"),
            where("sentTo", "==", selectedEmployee.email)
          );
          const mailSnap = await getDocs(mailQuery);
          
          let count = 0;
          for (const mailDoc of mailSnap.docs) {
            if (count >= 50) break;
            const mailData = mailDoc.data();
            if (mailData.message && mailData.message.text) {
              await updateDoc(doc(db, "mail", mailDoc.id), {
                "message.subject": `Välkommen till ${companyInfo?.companyName || "företaget"}!`,
                "message.text": mailData.message.text.replace(
                  /Hej .*!/,
                  `Hej ${firstName} ${lastName}!`
                ),
                updatedAt: serverTimestamp(),
              });
            }
            count++;
          }
        } catch (err) {
          console.log("Kunde inte uppdatera mail-kollektionen");
        }
 
        // 6. Uppdatera den lokala cachen och state
        const updatedOwner = {
          ...selectedEmployee,
          ...ownerUpdate,
          id: ownerDocId,
          authUid: ownerDocId,
          updatedAt: new Date(),
        };
 
        ownerInfoRef.current = updatedOwner;
        employeeCache.ownerInfo = updatedOwner;
 
        // 7. Uppdatera companyInfo
        setCompanyInfo((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            firstName: firstName,
            lastName: lastName,
            companyName: fullName,
          };
        });
 
        // 8. Uppdatera employees-listan
        setEmployees((previousEmployees) => {
          const ownerExists = previousEmployees.some(emp => emp.isOwner === true);
          
          if (ownerExists) {
            return previousEmployees.map((employee) =>
              employee.isOwner === true ? updatedOwner : employee
            );
          } else {
            const withoutOldOwner = previousEmployees.filter(
              (employee) => employee.isOwner !== true
            );
            return [...withoutOldOwner, updatedOwner].sort((a, b) => {
              const roleOrder = {
                "super-admin": 1,
                admin: 2,
                user: 3,
              };
              const orderA = roleOrder[a.role] || 99;
              const orderB = roleOrder[b.role] || 99;
              if (orderA === orderB) {
                return (a.name || "").localeCompare(b.name || "");
              }
              return orderA - orderB;
            });
          }
        });
 
        // 9. Försök uppdatera AuthContext-profilen om det är den inloggade användaren
        if (currentUser && currentUser.uid === ownerDocId) {
          try {
            // Försök att uppdatera via en global event eller callback
            console.log("Uppdaterar inloggad användares profil:", { firstName, lastName, fullName });
            
            // Om du har en global state eller context för användaren
            if (window.updateUserProfile) {
              window.updateUserProfile({
                firstName,
                lastName,
                displayName: fullName,
                name: fullName,
              });
            }
          } catch (err) {
            console.log("Kunde inte uppdatera AuthContext-profilen automatiskt");
          }
        }
 
      } else {
        // Hantera vanliga anställda (admin/user)
        const normalizedEmail = normalizeEmail(selectedEmployee.email);
 
        const updateData = {
          firstName,
          lastName,
          name: fullName,
          displayName: fullName,
          email: normalizedEmail,
          personalNumber: requiredNumber(selectedEmployee.personalNumber),
          employmentNumber: requiredNumber(selectedEmployee.employmentNumber),
          phoneNumber: selectedEmployee.phoneNumber?.trim() || "–",
          role: selectedEmployee.role || "user",
          workTimeId: selectedEmployee.workTimeId || "",
          groupId: selectedEmployee.groupId || "",
          membershipStatus: selectedEmployee.membershipStatus || "invited",
          companyId: companyInfo?.companyId || "",
          companyName: companyInfo?.companyName || "Företag",
          updatedAt: serverTimestamp(),
        };
 
        // Uppdatera i employed-kollektionen
        await updateDoc(
          doc(db, "users", accountToUse, "employed", selectedEmployee.id),
          updateData
        );
 
        // Uppdatera även i users-huvuddokumentet om det finns
        try {
          const userQuery = query(
            collection(db, "users"),
            where("email", "==", normalizedEmail)
          );
          const userSnap = await getDocs(userQuery);
          if (!userSnap.empty) {
            await updateDoc(doc(db, "users", userSnap.docs[0].id), {
              firstName,
              lastName,
              name: fullName,
              displayName: fullName,
              workTimeId: selectedEmployee.workTimeId || "",
              groupId: selectedEmployee.groupId || "",
              updatedAt: serverTimestamp(),
            });
          }
        } catch (err) {
          console.log("Kunde inte uppdatera users-huvuddokumentet");
        }
 
        // Uppdatera invitationTokens för denna anställd
        try {
          const tokensQuery = query(
            collection(db, "invitationTokens"),
            where("email", "==", normalizedEmail)
          );
          const tokensSnap = await getDocs(tokensQuery);
          
          for (const tokenDoc of tokensSnap.docs) {
            await updateDoc(doc(db, "invitationTokens", tokenDoc.id), {
              firstName,
              lastName,
              personalNumber: requiredNumber(selectedEmployee.personalNumber),
              employmentNumber: requiredNumber(selectedEmployee.employmentNumber),
              workTimeId: selectedEmployee.workTimeId || "",
              groupId: selectedEmployee.groupId || "",
              updatedAt: serverTimestamp(),
            });
          }
        } catch (err) {
          console.log("Inga invitationTokens hittades för denna email");
        }
      }
 
      // Uppdatera employeeCount
      await updateEmployeeCount(accountToUse);
      
      // Stäng popup och visa bekräftelse
      setShowEditPopup(false);
      setSelectedEmployee(null);
      alert("Ändringarna har sparats");
      
    } catch (error) {
      console.error("Fel vid uppdatering av användare:", error);
      alert(
        `Det gick inte att spara ändringarna. ${error?.code || ""} ${error?.message || ""}`.trim()
      );
    } finally {
      setIsSubmitting(false);
    }
  };
 
  const handleDeleteEmployee = async () => {
    const accountToUse = activeCompany?.companyAccountUid;
 
    if (!selectedEmployee || !accountToUse) return;
 
    if (selectedEmployee.role === "super-admin" && selectedEmployee.isOwner) {
      alert("Företagsägaren (Super-admin) kan inte tas bort.");
      return;
    }
 
    try {
      setIsSubmitting(true);
      const deleteEmployee = httpsCallable(getFunctions(), "deleteEmployee");
      await deleteEmployee({
        companyAccountUid: accountToUse,
        employeeDocId: selectedEmployee.id,
      });
 
      setShowDeleteConfirm(false);
      setShowEditPopup(false);
      setSelectedEmployee(null);
      
      console.log(`Anställd borttagen från företag ${accountToUse}`);
    } catch (error) {
      console.error("Fel vid borttagning av användare:", error);
      alert(error?.message || "Det gick inte att ta bort användaren.");
    } finally {
      setIsSubmitting(false);
    }
  };
 
  const handleNewEmployeeChange = (e) => {
    const { name, value } = e.target;
    setNewEmployee((prev) => ({
      ...prev,
      [name]: value,
    }));
    setSavingError("");
  };
 
  const handleSaveNewEmployee = async () => {
    try {
      setSavingError("");
 
      if (!newEmployee.firstName || !newEmployee.lastName) {
        setSavingError("Förnamn och efternamn måste fyllas i");
        return;
      }
 
      if (!newEmployee.email) {
        setSavingError("E-post måste fyllas i");
        return;
      }

      if (!requiredNumber(newEmployee.personalNumber)) {
        setSavingError("Personnummer måste fyllas i.");
        return;
      }

      if (!requiredNumber(newEmployee.employmentNumber)) {
        setSavingError("Anställningsnummer måste fyllas i.");
        return;
      }

      if (employmentNumberTaken(newEmployee.employmentNumber)) {
        setSavingError("Anställningsnumret är upptaget av en annan anställd.");
        return;
      }
 
      const accountToUse = activeCompany?.companyAccountUid;
 
      if (!accountToUse) {
        setSavingError("Inget företagskonto hittades.");
        return;
      }
 
      setIsSubmitting(true);
 
      const normalizedEmail = normalizeEmail(newEmployee.email);
      const token = generateToken();
 
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24);
 
      const emailSafeId = getSafeEmailDocId(normalizedEmail);
      const existingEmployeeSnap = await getDocs(
        query(
          collection(db, "users", accountToUse, "employed"),
          where("email", "==", normalizedEmail)
        )
      );
 
      if (!existingEmployeeSnap.empty) {
        setSavingError("En användare med den e-postadressen är redan inbjuden till detta företag.");
        setIsSubmitting(false);
        return;
      }
 
      await setDoc(doc(db, "invitationTokens", token), {
        token,
        email: normalizedEmail,
        firstName: newEmployee.firstName.trim(),
        lastName: newEmployee.lastName.trim(),
        role: newEmployee.role || "user",
        personalNumber: requiredNumber(newEmployee.personalNumber),
        employmentNumber: requiredNumber(newEmployee.employmentNumber),
        workTimeId: newEmployee.workTimeId || "",
        groupId: newEmployee.groupId || "",
        companyId: companyInfo?.companyId || "",
        companyName: companyInfo?.companyName || "Företag",
        companyUserId: accountToUse,
        invitedBy: accountToUse,
        createdAt: serverTimestamp(),
        expiresAt,
        used: false,
        usedAt: null,
      });
 
      const fullName = `${newEmployee.firstName.trim()} ${newEmployee.lastName.trim()}`;
 
      await setDoc(doc(db, "users", accountToUse, "employed", emailSafeId), {
        firstName: newEmployee.firstName.trim(),
        lastName: newEmployee.lastName.trim(),
        name: fullName,
        email: normalizedEmail,
        personalNumber: requiredNumber(newEmployee.personalNumber),
        employmentNumber: requiredNumber(newEmployee.employmentNumber),
        phoneNumber: newEmployee.phoneNumber?.trim() || "–",
        role: newEmployee.role || "user",
        workTimeId: newEmployee.workTimeId || "",
        groupId: newEmployee.groupId || "",
        membershipStatus: "invited",
        companyId: companyInfo?.companyId || "",
        companyName: companyInfo?.companyName || "Företag",
        invitedAt: serverTimestamp(),
        invitedBy: accountToUse,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        authUid: null,
      });
 
      const registerUrl = `${window.location.origin}/register?token=${token}&company=${accountToUse}`;
 
      await addDoc(collection(db, "mail"), {
        to: [normalizedEmail],
        message: {
          subject: `Välkommen till ${companyInfo?.companyName || "företaget"}!`,
          text: `Hej ${newEmployee.firstName.trim()} ${newEmployee.lastName.trim()}!
 
Du har blivit inbjuden att gå med i ${companyInfo?.companyName || "företaget"}.
 
Dina uppgifter:
- E-post: ${normalizedEmail}
- Roll: ${ROLE_LABELS_SV[newEmployee.role] || newEmployee.role}
 
Registrera dig här: ${registerUrl}
 
Länken är giltig i 24 timmar och kan endast användas en gång.
 
Vänliga hälsningar,
${companyInfo?.companyName || "Företaget"}`,
          html: `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
            </head>
            <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
              <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e5e7eb; border-radius: 12px;">
                <h2 style="color: #2563eb; margin-bottom: 20px;">Välkommen till ${companyInfo?.companyName || "företaget"}!</h2>
                <p>Hej <strong>${newEmployee.firstName.trim()} ${newEmployee.lastName.trim()}</strong>,</p>
                <p>Du har blivit inbjuden att gå med i företaget. Klicka på knappen nedan för att registrera dig och skapa ditt konto.</p>
                <div style="background: #f9fafb; padding: 15px; border-radius: 8px; margin: 20px 0;">
                  <h3 style="margin-top: 0; color: #111827;">Dina uppgifter:</h3>
                  <ul style="margin: 0; padding-left: 20px;">
                    <li><strong>E-post:</strong> ${normalizedEmail}</li>
                    <li><strong>Roll:</strong> ${ROLE_LABELS_SV[newEmployee.role] || newEmployee.role}</li>
                  </ul>
                </div>
                <p><strong>Viktigt:</strong> Länken är giltig i 24 timmar och kan endast användas en gång.</p>
                <div style="text-align: center; margin: 30px 0;">
                  <a href="${registerUrl}" 
                     style="background-color: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: 600;">
                    Registrera dig här →
                  </a>
                </div>
                <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 20px 0;">
                <p style="font-size: 12px; color: #6b7280; text-align: center;">
                  Vänliga hälsningar,<br>
                  ${companyInfo?.companyName || "Företaget"}
                </p>
              </div>
            </body>
            </html>
          `,
        },
        delivery: {
          startTime: serverTimestamp(),
          state: "PENDING",
        },
        companyId: companyInfo?.companyId || "",
        companyName: companyInfo?.companyName || "Företag",
        companyUserId: accountToUse,
        sentTo: normalizedEmail,
        sentBy: accountToUse,
        sentAt: serverTimestamp(),
        type: "employee-invitation",
      });
 
      await updateEmployeeCount(accountToUse);
 
      setShowAddPopup(false);
      setNewEmployee({
        firstName: "",
        lastName: "",
        email: "",
        personalNumber: "",
        employmentNumber: "",
        phoneNumber: "",
        role: "user",
        workTimeId: "",
        groupId: "",
      });
 
      alert(`Inbjudan skickades till ${normalizedEmail}!`);
    } catch (error) {
      console.error("Fel vid skick av inbjudan:", error);
      setSavingError(
        error.message || "Det gick inte att skicka inbjudan via e-post."
      );
    } finally {
      setIsSubmitting(false);
    }
  };
 
  const yearOptions = getYearOptions();
  const monthOptions = getMonthOptions();
 
  if (companyLoading || loading) {
    return <LoadingIndicator message="Laddar anställda..." fullPage />;
  }
 
  if (!activeCompany) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
        Inget företag valt. Vänligen logga in igen.
      </div>
    );
  }
 
  return (
    <div style={{ padding: "0 0 24px" }}>
      <div style={{ marginBottom: 16, padding: "0 0 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Visar anställda för: <strong>{activeCompany.companyName}</strong>
        </p>
      </div>
 
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 0 20px",
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          flexWrap: "nowrap",
        }}
      >
        <div
          style={{
            ...boxStyle,
            minWidth: 320,
            maxWidth: 420,
            flex: "1 1 360px",
          }}
        >
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Sök på namn, email, roll, personnummer, anställningsnummer eller telefon..."
            style={inputStyle}
          />
        </div>

        <div style={boxStyle}>
          <select
            aria-label="Filtrera på roll"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{ ...inputStyle, appearance: "none" }}
          >
            <option value="all">Alla roller</option>
            <option value="user">Användare</option>
            <option value="admin">Admin och Super-admin</option>
          </select>
          <FaChevronDown style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: 12, color: "#6b7280" }} />
          <span style={labelStyle}>Roll</span>
        </div>

        <div style={boxStyle}>
          <select
            aria-label="Filtrera på grupp"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            style={{ ...inputStyle, appearance: "none" }}
          >
            <option value="all">Alla grupper</option>
            {employeeGroups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
            <option value="ungrouped">Ingen grupp</option>
          </select>
          <FaChevronDown style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: 12, color: "#6b7280" }} />
          <span style={labelStyle}>Grupp</span>
        </div>
 
        <div style={boxStyle}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            <option value="all">Alla statusar</option>
            <option value="active">Aktiva</option>
            <option value="invited">Inbjudna</option>
          </select>
 
          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>Status</span>
        </div>
 
        <div style={boxStyle}>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            {yearOptions.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
 
          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>År</span>
        </div>
 
        <div style={boxStyle}>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            {monthOptions.map(month => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>
 
          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>Månad</span>
        </div>
 
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            flexDirection: "row",
            flexWrap: "nowrap",
            gap: 4,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setShowAddPopup(true)}
            aria-label="Lägg till anställd"
            title="Lägg till anställd"
            style={{
              ...toolbarButtonStyle,
              background: "#3b82f6",
              border: "1px solid #2563eb",
            }}
          >
            <FaPlus />
          </button>
          <button
            onClick={() => setShowSchedulingPopup(true)}
            aria-label={unassignedEmployeeCount ? `Öppna scheman, ${unassignedEmployeeCount} personer utan schema` : "Öppna scheman"}
            title={unassignedEmployeeCount ? `Hantera scheman och personer – ${unassignedEmployeeCount} personer utan schema` : "Hantera scheman och personer"}
            style={{
              ...toolbarButtonStyle,
              position: "relative",
              background: "#6366f1",
              border: "1px solid #4f46e5",
            }}
          >
            <FaCalendarPlus />
            {unassignedEmployeeCount > 0 && <span className="employee-scheduling-badge" aria-hidden="true">{unassignedEmployeeCount > 9 ? "9+" : unassignedEmployeeCount}</span>}
          </button>
        </div>
      </div>
 
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "16px 18px",
          marginBottom: 14,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            color: "#111827",
          }}
        >
          Anställda {!loading && `(${filteredEmployees.length} av ${employees.length})`}
        </h1>
        {!loading && employees.length > 0 && (
          <p style={{ margin: "8px 0 0 0", fontSize: 14, color: "#6b7280" }}>
            Visar {filteredEmployees.length} av {employees.length} anställda
          </p>
        )}
      </div>
 
      <div className="employee-table"
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          overflowX: "auto",
        }}
      >
        <div className="employee-table-header"
          style={{
            display: "grid",
            gridTemplateColumns: EMPLOYEE_TABLE_COLUMNS,
            gap: 12,
            padding: "10px 14px",
            minWidth: 1140,
            borderBottom: "1px solid #e5e7eb",
            borderLeft: "4px solid transparent",
            background: "#f9fafb",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
            ANSTÄLLNINGSNR
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
            NAMN
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
            ARBETSTIDSMÅTT
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
            PERSONNUMMER
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
            ROLL
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
            STATUS
          </div>
          <div></div>
        </div>
 
        {loading ? (
          <LoadingIndicator message="Laddar anställda..." />
        ) : filteredEmployees.length > 0 ? (
          groupedEmployees.flatMap((section) => [
            <div className="employee-section-heading" key={`group-${section.id}`}>
              <strong>{section.name}</strong>
              <span>{section.employees.length} {section.employees.length === 1 ? "person" : "personer"}</span>
            </div>,
            ...section.employees.map((emp) => {
            const workTime = workTimeOptions.find((option) => option.id === emp.workTimeId);
            const isExpired = emp.membershipStatus === "expired";
            const isPending = emp.membershipStatus === "pending";
            const isInvited = emp.membershipStatus === "invited";
            const isDisabled = emp.membershipStatus === "disabled";
            const isActive = emp.membershipStatus === "active";
            const isSuperAdmin = emp.role === "super-admin";
            const isOwner = emp.isOwner === true;

            let background = "#ffffff";
            let borderLeftColor = "transparent";

            if (isExpired) {
              background = "#fee2e2";
              borderLeftColor = "#ef4444";
            } else if (isPending || isInvited) {
              background = "#fef3c7";
              borderLeftColor = "#f59e0b";
            } else if (isDisabled) {
              background = "#f3f4f6";
              borderLeftColor = "#9ca3af";
            } else if (isActive) {
              background = "#f0fdf4";
              borderLeftColor = "#22c55e";
            }

            if (isSuperAdmin && isOwner) {
              background = "#eff6ff";
              borderLeftColor = "#3b82f6";
            }
 
            return (
              <div className="employee-table-row"
                key={emp.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: EMPLOYEE_TABLE_COLUMNS,
                  gap: 12,
                  alignItems: "center",
                  padding: "6px 14px",
                  minWidth: 1140,
                  borderBottom: "1px solid #f1f5f9",
                  background,
                  borderLeft: `4px solid ${borderLeftColor}`,
                }}
              >
                <div className="employee-table-cell">{emp.employmentNumber}</div>
                <div className="employee-table-cell employee-table-name">{emp.name}</div>
                <div className="employee-table-cell">{workTime ? workTimeLabel(workTime) : "–"}</div>
                <div className="employee-table-cell">{emp.personalNumber}</div>
                <div className="employee-table-cell">{ROLE_LABELS_SV[emp.role] || emp.role}</div>
                <div>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 25,
                      padding: "0 10px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 700,
                      background:
                        emp.membershipStatus === "active"
                          ? "#dcfce7"
                          : emp.membershipStatus === "pending" ||
                            emp.membershipStatus === "invited"
                          ? "#fef3c7"
                          : emp.membershipStatus === "expired"
                          ? "#fee2e2"
                          : "#f3f4f6",
                      color:
                        emp.membershipStatus === "active"
                          ? "#166534"
                          : emp.membershipStatus === "pending" ||
                            emp.membershipStatus === "invited"
                          ? "#92400e"
                          : emp.membershipStatus === "expired"
                          ? "#991b1b"
                          : "#4b5563",
                    }}
                  >
                    {STATUS_LABELS_SV[emp.membershipStatus] ||
                      emp.membershipStatus}
                  </span>
                </div>
 
                <div style={{ textAlign: "right" }}>
                  <button
                    onClick={() => {
                      setSelectedEmployee(emp);
                      setShowDetailsPopup(true);
                    }}
                    aria-label={`Visa ${emp.name}`}
                    style={{
                      background: "none",
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      width: 32,
                      height: 32,
                      cursor: "pointer",
                      fontSize: 18,
                      color: "#374151",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Visa personuppgifter"
                  >
                    ⋮
                  </button>
                </div>
              </div>
            );
            }),
          ])
        ) : (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: "#6b7280",
              fontWeight: 500,
            }}
          >
            {activeCompany?.companyAccountUid
              ? "Inga anställda hittades för detta företag."
              : "Inget företags-ID hittades. Var vänlig logga in igen."}
          </div>
        )}
      </div>
 
      {activeCompany?.companyAccountUid && <ConnectedSchedulingPopup
        key={activeCompany.companyAccountUid}
        isOpen={showSchedulingPopup}
        onUnassignedCountChange={setUnassignedEmployeeCount}
        onClose={() => setShowSchedulingPopup(false)}
        companyUid={activeCompany.companyAccountUid}
        role={activeCompany.role}
        allowedSchemaIds={allowedSchemaIds}
        permissionsLoading={permissionsLoading}
      />}

      {showDetailsPopup && selectedEmployee && (
        <div className="employee-details-backdrop" onClick={() => setShowDetailsPopup(false)}>
          <div className="employee-details-dialog" role="dialog" aria-modal="true" aria-label={`Information om ${selectedEmployee.name}`} onClick={(event) => event.stopPropagation()}>
            <div className="employee-details-header">
              <h2>{selectedEmployee.name}</h2>
              <button type="button" onClick={() => setShowDetailsPopup(false)} aria-label="Stäng personuppgifter"><FaTimes /></button>
            </div>
            <div className="employee-details-tabs">
              <span className="active">Personuppgifter</span>
            </div>
            <div className="employee-details-content">
                <div className="employee-details-personal">
                  <div className="employee-details-avatar" aria-hidden="true">{(selectedEmployee.firstName?.[0] || selectedEmployee.name?.[0] || "?").toUpperCase()}</div>
                  <div className="employee-details-main">
                    <dl className="employee-details-fields">
                      <dt>Namn</dt><dd>{selectedEmployee.name || "–"}</dd>
                      <dt>Email</dt><dd>{selectedEmployee.email && selectedEmployee.email !== "E-post saknas" ? <a href={`mailto:${selectedEmployee.email}`}>{selectedEmployee.email}</a> : "–"}</dd>
                      <dt>Personnummer</dt><dd>{selectedEmployee.personalNumber || "–"}</dd>
                      <dt>Anställningsnr</dt><dd>{selectedEmployee.employmentNumber || "–"}</dd>
                      <dt>Telefon</dt><dd>{selectedEmployee.phoneNumber && selectedEmployee.phoneNumber !== "–" ? <a href={`tel:${selectedEmployee.phoneNumber}`}>{selectedEmployee.phoneNumber}</a> : "–"}</dd>
                      <dt>Roll</dt><dd>{ROLE_LABELS_SV[selectedEmployee.role] || selectedEmployee.role}</dd>
                      <dt>Arbetstidsmått</dt><dd>{workTimeOptions.find((option) => option.id === selectedEmployee.workTimeId) ? workTimeLabel(workTimeOptions.find((option) => option.id === selectedEmployee.workTimeId)) : "–"}</dd>
                      <dt>Grupp</dt><dd>{employeeGroups.find((group) => group.id === selectedEmployee.groupId)?.name || "Ingen grupp"}</dd>
                      <dt>Status</dt><dd>{STATUS_LABELS_SV[selectedEmployee.membershipStatus] || selectedEmployee.membershipStatus || "–"}</dd>
                    </dl>
                  </div>
                  {canEditEmployee(selectedEmployee) && (
                    <button type="button" className="employee-details-edit" onClick={() => handleEditClick(selectedEmployee)}>Ändra personuppgifter</button>
                  )}
                </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Popup */}
      {showAddPopup && (
        <div
          onClick={() => {
            if (isSubmitting) return;
            setShowAddPopup(false);
            setNewEmployee({
              firstName: "",
              lastName: "",
              email: "",
              personalNumber: "",
              employmentNumber: "",
              phoneNumber: "",
              role: "user",
              workTimeId: "",
              groupId: "",
            });
            setSavingError("");
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: 560,
              maxWidth: "90vw",
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
              border: "1px solid #e5e7eb",
              position: "relative",
            }}
          >
            <h3
              style={{
                margin: "0 0 18px",
                fontSize: 22,
                color: "#111827",
              }}
            >
              Lägg till anställd
            </h3>
 
            {savingError && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  color: "#991b1b",
                  padding: "10px",
                  borderRadius: "6px",
                  marginBottom: "16px",
                  fontSize: "14px",
                }}
              >
                {savingError}
              </div>
            )}
 
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
              }}
            >
              <input
                name="firstName"
                value={newEmployee.firstName}
                onChange={handleNewEmployeeChange}
                placeholder="Förnamn *"
                style={inputStyle}
                disabled={isSubmitting}
              />
              <input
                name="lastName"
                value={newEmployee.lastName}
                onChange={handleNewEmployeeChange}
                placeholder="Efternamn *"
                style={inputStyle}
                disabled={isSubmitting}
              />
              <input
                name="email"
                value={newEmployee.email}
                onChange={handleNewEmployeeChange}
                placeholder="Email *"
                type="email"
                style={inputStyle}
                disabled={isSubmitting}
              />
              <input
                name="personalNumber"
                value={newEmployee.personalNumber}
                onChange={handleNewEmployeeChange}
                placeholder="Personnummer (ÅÅÅÅMMDD-XXXX) *"
                required
                style={inputStyle}
                disabled={isSubmitting}
              />
              <div>
                <input
                  name="employmentNumber"
                  value={newEmployee.employmentNumber}
                  onChange={handleNewEmployeeChange}
                  placeholder="Anställningsnummer *"
                  required
                  aria-invalid={!isSubmitting && employmentNumberTaken(newEmployee.employmentNumber)}
                  style={inputStyle}
                  disabled={isSubmitting}
                />
                {!isSubmitting && employmentNumberTaken(newEmployee.employmentNumber) && (
                  <span className="employee-number-error" role="alert">Anställningsnumret är upptaget.</span>
                )}
              </div>
              <input
                name="phoneNumber"
                value={newEmployee.phoneNumber}
                onChange={handleNewEmployeeChange}
                placeholder="Telefonnummer (frivilligt)"
                style={inputStyle}
                disabled={isSubmitting}
              />
 
              <WorkTimeField value={newEmployee.workTimeId} options={workTimeOptions}
                onChange={(workTimeId) => setNewEmployee((current) => ({ ...current, workTimeId }))}
                onCreate={createWorkTime} onDelete={deleteWorkTime} disabled={isSubmitting} />
              <EmployeeGroupField value={newEmployee.groupId} groups={employeeGroups}
                onChange={(groupId) => setNewEmployee((current) => ({ ...current, groupId }))}
                onCreate={createEmployeeGroup} onDelete={deleteEmployeeGroup} disabled={isSubmitting} />
              <div style={{ gridColumn: "span 2" }}>
                <label
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginBottom: 4,
                    display: "block",
                  }}
                >
                  Roll
                </label>
                <select
                  name="role"
                  value={newEmployee.role}
                  onChange={handleNewEmployeeChange}
                  style={{ ...inputStyle, paddingRight: 12 }}
                  disabled={isSubmitting}
                >
                  <option value="user">Användare</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
 
            <div
              style={{
                fontSize: 12,
                color: "#6b7280",
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              * Obligatoriska fält
            </div>
 
            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 18,
              }}
            >
              <button
                onClick={handleSaveNewEmployee}
                disabled={isSubmitting || employmentNumberTaken(newEmployee.employmentNumber)}
                style={{
                  height: 42,
                  padding: "0 16px",
                  borderRadius: 10,
                  border: "none",
                  background: isSubmitting || employmentNumberTaken(newEmployee.employmentNumber) ? "#93c5fd" : "#2563eb",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: isSubmitting || employmentNumberTaken(newEmployee.employmentNumber) ? "not-allowed" : "pointer",
                }}
              >
                {isSubmitting ? "Skickar..." : "Skicka inbjudan"}
              </button>
 
              <button
                onClick={() => {
                  if (isSubmitting) return;
                  setShowAddPopup(false);
                  setNewEmployee({
                    firstName: "",
                    lastName: "",
                    email: "",
                    personalNumber: "",
                    employmentNumber: "",
                    phoneNumber: "",
                    role: "user",
                    workTimeId: "",
                    groupId: "",
                  });
                  setSavingError("");
                }}
                disabled={isSubmitting}
                style={{
                  height: 42,
                  padding: "0 16px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  color: "#111827",
                  fontWeight: 600,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  opacity: isSubmitting ? 0.7 : 1,
                }}
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* Edit Popup */}
      {showEditPopup && selectedEmployee && (
        <div
          onClick={() => {
            setShowEditPopup(false);
            setSelectedEmployee(null);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: 560,
              maxWidth: "90vw",
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
              border: "1px solid #e5e7eb",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 22,
                  color: "#111827",
                }}
              >
                {selectedEmployee.isOwner
                  ? "Redigera mina personuppgifter"
                  : "Redigera anställd"}
              </h3>
              <button
                onClick={() => {
                  setShowEditPopup(false);
                  setSelectedEmployee(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 20,
                  color: "#6b7280",
                }}
              >
                <FaTimes />
              </button>
            </div>

            {editError && <div className="employee-form-error" role="alert">{editError}</div>}
 
            <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 14,
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      FÖRNAMN
                    </label>
                    <input
                      value={selectedEmployee.firstName || ""}
                      onChange={(e) =>
                        setSelectedEmployee({
                          ...selectedEmployee,
                          firstName: e.target.value,
                        })
                      }
                      style={inputStyle}
                      placeholder="Förnamn"
                    />
                  </div>
 
                  <div>
                    <label
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      EFTERNAMN
                    </label>
                    <input
                      value={selectedEmployee.lastName || ""}
                      onChange={(e) =>
                        setSelectedEmployee({
                          ...selectedEmployee,
                          lastName: e.target.value,
                        })
                      }
                      style={inputStyle}
                      placeholder="Efternamn"
                    />
                  </div>
 
                  <div>
                    <label
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      EMAIL
                    </label>
                    <input
                      value={
                        selectedEmployee.email === "E-post saknas"
                          ? ""
                          : selectedEmployee.email
                      }
                      onChange={(e) =>
                        setSelectedEmployee({
                          ...selectedEmployee,
                          email: e.target.value,
                        })
                      }
                      disabled={selectedEmployee.isOwner}
                      style={{
                        ...inputStyle,
                        ...(selectedEmployee.isOwner
                          ? { background: "#f3f4f6", cursor: "not-allowed" }
                          : {}),
                      }}
                      placeholder="Email"
                    />
                  </div>
 
                  <div>
                    <label
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      PERSONNUMMER *
                    </label>
                    <input
                      value={
                        selectedEmployee.personalNumber === "–"
                          ? ""
                          : selectedEmployee.personalNumber
                      }
                      onChange={(e) =>
                        setSelectedEmployee({
                          ...selectedEmployee,
                          personalNumber: e.target.value,
                        })
                      }
                      placeholder="ÅÅÅÅMMDD-XXXX"
                      required
                      style={inputStyle}
                    />
                  </div>
 
                  <div>
                    <label
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      ANSTÄLLNINGSNUMMER *
                    </label>
                    <input
                      value={
                        selectedEmployee.employmentNumber === "–"
                          ? ""
                          : selectedEmployee.employmentNumber
                      }
                      onChange={(e) =>
                        setSelectedEmployee({
                          ...selectedEmployee,
                          employmentNumber: e.target.value,
                        })
                      }
                      placeholder="Anställningsnummer"
                      required
                      aria-invalid={employmentNumberTaken(selectedEmployee.employmentNumber, selectedEmployee.id)}
                      style={inputStyle}
                    />
                    {employmentNumberTaken(selectedEmployee.employmentNumber, selectedEmployee.id) && (
                      <span className="employee-number-error" role="alert">Anställningsnumret är upptaget.</span>
                    )}
                  </div>
 
                  <div>
                    <label
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      TELEFONNUMMER
                    </label>
                    <input
                      value={
                        selectedEmployee.phoneNumber === "–"
                          ? ""
                          : selectedEmployee.phoneNumber
                      }
                      onChange={(e) =>
                        setSelectedEmployee({
                          ...selectedEmployee,
                          phoneNumber: e.target.value,
                        })
                      }
                      placeholder="Telefonnummer"
                      style={inputStyle}
                    />
                  </div>
 
                  <div>
                    <label
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      SKAPADES
                    </label>
                    <input
                      value={
                        selectedEmployee.createdAt
                          ? formatDate(selectedEmployee.createdAt)
                          : ""
                      }
                      disabled
                      style={{
                        ...inputStyle,
                        background: "#f3f4f6",
                        cursor: "not-allowed",
                      }}
                    />
                  </div>
 
                  <WorkTimeField value={selectedEmployee.workTimeId} options={workTimeOptions}
                onChange={(workTimeId) => setSelectedEmployee((current) => ({ ...current, workTimeId }))}
                onCreate={createWorkTime} onDelete={deleteWorkTime} disabled={isSubmitting} />
              <EmployeeGroupField value={selectedEmployee.groupId} groups={employeeGroups}
                    onChange={(groupId) => setSelectedEmployee((current) => ({ ...current, groupId }))}
                    onCreate={createEmployeeGroup} onDelete={deleteEmployeeGroup} disabled={isSubmitting} />
                  <div style={{ gridColumn: "span 2" }}>
                    <label
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Roll
                    </label>
                    <select
                      value={selectedEmployee.role}
                      onChange={(e) =>
                        setSelectedEmployee({
                          ...selectedEmployee,
                          role: e.target.value,
                        })
                      }
                      disabled={selectedEmployee.isOwner}
                      style={{
                        ...inputStyle,
                        paddingRight: 12,
                        ...(selectedEmployee.isOwner
                          ? { background: "#f3f4f6", cursor: "not-allowed" }
                          : {}),
                      }}
                    >
                      <option value="user">Användare</option>
                      <option value="admin">Admin</option>
                      {selectedEmployee.isOwner && (
                        <option value="super-admin">Super-admin</option>
                      )}
                    </select>
                  </div>
                </div>
 
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    marginTop: 18,
                  }}
                >
                  <button
                    onClick={handleSaveEmployee}
                    disabled={isSubmitting || employmentNumberTaken(selectedEmployee.employmentNumber, selectedEmployee.id)}
                    style={{
                      height: 42,
                      padding: "0 16px",
                      borderRadius: 10,
                      border: "none",
                      background: isSubmitting || employmentNumberTaken(selectedEmployee.employmentNumber, selectedEmployee.id) ? "#93c5fd" : "#2563eb",
                      color: "#fff",
                      fontWeight: 700,
                      cursor: isSubmitting || employmentNumberTaken(selectedEmployee.employmentNumber, selectedEmployee.id) ? "not-allowed" : "pointer",
                    }}
                  >
                    {isSubmitting ? "Sparar..." : "Spara ändringar"}
                  </button>
 
                  <button
                    onClick={() => {
                      setShowEditPopup(false);
                      setSelectedEmployee(null);
                    }}
                    style={{
                      height: 42,
                      padding: "0 16px",
                      borderRadius: 10,
                      border: "1px solid #d1d5db",
                      background: "#fff",
                      color: "#111827",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Avbryt
                  </button>
 
                  {!selectedEmployee.isOwner &&
                    selectedEmployee.membershipStatus === "invited" && (
                    <button
                      onClick={() => setShowResendConfirm(true)}
                      style={{
                        height: 42,
                        padding: "0 16px",
                        borderRadius: 10,
                        border: "none",
                        background: "#f59e0b",
                        color: "#fff",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Skicka om inbjudan
                    </button>
                  )}
 
                  {!selectedEmployee.isOwner && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    style={{
                      height: 42,
                      padding: "0 16px",
                      borderRadius: 10,
                      border: "none",
                      background: "#ef4444",
                      color: "#fff",
                      fontWeight: 700,
                      cursor: "pointer",
                      marginLeft:
                        selectedEmployee.membershipStatus === "invited"
                          ? 0
                          : "auto",
                    }}
                  >
                    Radera
                  </button>
                  )}
                </div>
              </>
          </div>
        </div>
      )}
 
      {/* Delete Confirm Popup */}
      {showDeleteConfirm && selectedEmployee && (
        <div
          onClick={() => setShowDeleteConfirm(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 4000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: 400,
              maxWidth: "90vw",
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
              border: "1px solid #e5e7eb",
            }}
          >
            <h3
              style={{
                margin: "0 0 12px",
                fontSize: 20,
                color: "#111827",
              }}
            >
              Bekräfta borttagning
            </h3>
 
            <p style={{ margin: "0 0 24px", color: "#4b5563" }}>
              Är du säker på att du vill ta bort {selectedEmployee.firstName}{" "}
              {selectedEmployee.lastName} från företaget? Denna åtgärd kan inte ångras.
            </p>
 
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  height: 42,
                  padding: "0 16px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  color: "#111827",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Avbryt
              </button>
 
              <button
                onClick={handleDeleteEmployee}
                style={{
                  height: 42,
                  padding: "0 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "#ef4444",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Ja, radera
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* Resend Confirm Popup */}
      {showResendConfirm && selectedEmployee && (
        <div
          onClick={() => setShowResendConfirm(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 4000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: 400,
              maxWidth: "90vw",
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
              border: "1px solid #e5e7eb",
            }}
          >
            <h3
              style={{
                margin: "0 0 12px",
                fontSize: 20,
                color: "#111827",
              }}
            >
              Skicka om inbjudan
            </h3>
 
            <p style={{ margin: "0 0 24px", color: "#4b5563" }}>
              Är du säker på att du vill skicka en ny inbjudan till{" "}
              {selectedEmployee.firstName} {selectedEmployee.lastName}? Den gamla
              inbjudningslänken kommer att upphöra att gälla.
            </p>
 
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowResendConfirm(false)}
                style={{
                  height: 42,
                  padding: "0 16px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  color: "#111827",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Avbryt
              </button>
 
              <button
                onClick={handleResendInvitation}
                disabled={isSubmitting}
                style={{
                  height: 42,
                  padding: "0 16px",
                  borderRadius: 10,
                  border: "none",
                  background: isSubmitting ? "#fcd34d" : "#f59e0b",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                }}
              >
                {isSubmitting ? "Skickar..." : "Ja, skicka om"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles live with this component; keep selectors and responsive rules together.
registerStyles("EmployeesPage", `
.employee-details-backdrop {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, .46);
  box-sizing: border-box;
}

.employee-details-dialog {
  width: min(100%, 1100px);
  max-height: min(90vh, 800px);
  overflow: auto;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 20px 60px rgba(15, 23, 42, .2);
  color: #3d4654;
}

.employee-details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 32px;
  border-bottom: 1px solid #dfe6f0;
}

.employee-details-header h2 { margin: 0; font-size: 25px; }
.employee-details-header button { border: 0; background: none; color: #3d4654; font-size: 24px; cursor: pointer; }

.employee-details-tabs {
  display: flex;
  gap: 24px;
  padding: 0 32px;
  border-bottom: 1px solid #dfe6f0;
}

.employee-details-tabs span {
  padding: 20px 4px 18px;
  border: 0;
  border-bottom: 3px solid transparent;
  background: none;
  color: #68768b;
  font-size: 13px;
  font-weight: 600;
}

.employee-details-tabs span.active { border-bottom-color: #4c86db; color: #4379c3; }
.employee-details-content { padding: 32px; min-height: 260px; font-size: 14px; }
.employee-details-personal { display: flex; align-items: flex-start; gap: 28px; }
.employee-details-avatar {
  display: grid;
  place-items: center;
  flex: 0 0 140px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: #edf1f7;
  color: #929dad;
  font-size: 64px;
  font-weight: 700;
}
.employee-details-main { flex: 1; min-width: 0; }
.employee-details-fields { display: grid; grid-template-columns: max-content minmax(0, 1fr); gap: 12px 24px; margin: 0; }
.employee-details-fields dt { font-weight: 700; }
.employee-details-fields dd { min-width: 0; margin: 0; overflow-wrap: anywhere; }
.employee-details-fields a { color: #4379c3; text-decoration: none; }
.employee-details-edit { align-self: flex-start; padding: 12px 16px; border: 1px solid #d5deeb; border-radius: 6px; background: #fff; color: #3d4654; font-size: 13px; font-weight: 600; white-space: nowrap; cursor: pointer; }
.employee-details-edit:hover { background: #f1f6ff; }

.employee-table-cell {
  overflow: hidden;
  color: #374151;
  font-size: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.employee-table-name { color: #111827; font-weight: 500; }

@media (max-width: 750px) {
  .employee-details-backdrop { padding: 12px; }
  .employee-details-header, .employee-details-content { padding: 20px; }
  .employee-details-tabs { padding: 0 20px; }
  .employee-details-personal { flex-wrap: wrap; gap: 20px; }
  .employee-details-avatar { flex-basis: 88px; width: 88px; height: 88px; font-size: 40px; }
  .employee-details-edit { order: -1; margin-left: auto; }
  .employee-details-main { flex-basis: 100%; }
  .employee-details-fields { grid-template-columns: 1fr; gap: 4px; }
  .employee-details-fields dd { margin-bottom: 12px; }
}

.employee-table-header {
  border-top: 4px solid #3b63a5;
  background: #f1f6ff !important;
}

.employee-section-heading {
  display: flex;
  align-items: baseline;
  gap: 12px;
  min-width: 1140px;
  padding: 26px 14px 14px;
  border-bottom: 1px solid #dfe6f0;
  color: #3d4654;
}

.employee-section-heading strong {
  font-size: 18px;
  font-weight: 700;
}

.employee-section-heading span {
  color: #748197;
  font-size: 13px;
}

.employee-group-add {
  display: grid;
  place-items: center;
  flex: 0 0 50px;
  height: 50px;
  border: 0;
  border-radius: 8px;
  background: #4f46e5;
  color: #fff;
  cursor: pointer;
}

.employee-group-add:disabled { opacity: .55; cursor: not-allowed; }

.employee-group-panel {
  position: absolute;
  z-index: 1;
  top: 0;
  left: calc(100% + 12px);
  width: 320px;
  min-height: 100%;
  max-height: min(80vh, 650px);
  overflow-y: auto;
  padding: 22px;
  box-sizing: border-box;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(0, 0, 0, .18);
  animation: employee-group-slide .25s ease-out;
}

@keyframes employee-group-slide {
  from { opacity: 0; transform: translateX(-18px); }
  to { opacity: 1; transform: translateX(0); }
}

.employee-group-panel-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.employee-group-panel-header h4 { margin: 0; font-size: 18px; color: #111827; }
.employee-group-panel-header button { border: 0; background: #f3f4f6; border-radius: 7px; width: 32px; height: 32px; cursor: pointer; }
.employee-group-panel p { color: #6b7280; font-size: 13px; line-height: 1.5; }
.employee-group-panel form { display: grid; gap: 8px; margin-top: 24px; }
.employee-group-panel label { color: #374151; font-size: 13px; font-weight: 600; }
.employee-group-panel input { width: 100%; height: 44px; padding: 0 12px; box-sizing: border-box; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; }
.employee-group-error { color: #b91c1c; font-size: 12px; }
.employee-group-panel-actions { display: flex; gap: 8px; margin-top: 14px; }
.employee-group-panel-actions button { padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; cursor: pointer; }
.employee-group-panel-actions button[type="submit"] { border-color: #4f46e5; background: #4f46e5; color: #fff; }
.employee-group-panel-actions button:disabled { opacity: .55; cursor: not-allowed; }

.employee-group-list { margin-top: 24px; padding-top: 18px; border-top: 1px solid #e5e7eb; }
.employee-group-list h5 { margin: 0 0 10px; font-size: 13px; color: #374151; }
.employee-group-list-items { max-height: 196px; overflow-y: auto; }
.employee-group-list-items > p { margin: 0; }
.employee-group-list-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; height: 49px; padding: 9px 0; box-sizing: border-box; border-bottom: 1px solid #f3f4f6; font-size: 14px; color: #111827; }
.employee-group-list-row span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.employee-group-list-row button { display: grid; place-items: center; flex: 0 0 30px; width: 30px; height: 30px; border: 0; border-radius: 7px; background: #fef2f2; color: #b91c1c; cursor: pointer; }
.employee-group-list-row button:hover { background: #fee2e2; }
.employee-group-list-row button:disabled { opacity: .5; cursor: not-allowed; }

.employee-number-error { display: block; margin-top: 4px; color: #b91c1c; font-size: 12px; }
.employee-form-error { margin-bottom: 16px; padding: 10px; border-radius: 6px; background: #fee2e2; color: #991b1b; font-size: 14px; }

@media (max-width: 1250px) {
  .employee-group-panel { left: auto; right: 0; width: min(100%, 340px); }
}

@media (prefers-reduced-motion: reduce) {
  .employee-group-panel { animation: none; }
}
.employee-scheduling-badge {
  position: absolute;
  right: -5px;
  top: -5px;
  min-width: 18px;
  height: 18px;
  padding: 0 3px;
  box-sizing: border-box;
  display: grid;
  place-items: center;
  border-radius: 999px;
  border: 2px solid white;
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 700;
  pointer-events: none;
}
`);
